import { useEffect, useState } from 'react';
import CRMService from '@utility/services/crm';
import {
  CommonConstant,
  ErrorMessageConstant,
} from '@utility/constants/common';
import { useDebouncedEffect } from './debounceAsync';

interface ICustomerAddress {
  addressLine1: string;
  addressLine2?: string;
  city?: string;
  stateOrRegion?: string;
  postalCode?: string;
  districtOrCounty?: string;
  countryCode?: string;
}

interface IMarketplace {
  _id: string;
  name: string;
  type: string;
  alias: string;
}

interface ICustomer {
  _id: string;
  manual: boolean;
  marketplaces: IMarketplace[];
  name: string;
  email: string;
  phone: string;
  organization_id: string;
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
  totalOrders: number;
  totalOrdersValue: number;
  address: ICustomerAddress;
}

interface ICustomerListResponse {
  limit: number;
  page_no: number;
  total_data: number;
  total_pages: number;
  data: ICustomer[];
}

export const customerHook = () => {
  const [data, setData] = useState<ICustomer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState({
    limit: CommonConstant.defaultPageSize,
    page: 1,
    search: '',
    sort: {
      order_value_sort: CommonConstant.sortDirection.descending,
      total_order_sort: CommonConstant.sortDirection.descending,
    },
  });
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const temp: any = {
        ...filter,
        ...filter.sort,
        sort: null,
      };

      if (!temp.search) {
        delete temp.search;
      }

      const result: ICustomerListResponse = await CRMService.get(temp);
      setData(result.data);
      setTotalPages(result.total_pages);
      setTotalData(result.total_data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      fetchData();
    },
    [filter],
    500,
  );

  const pageChanged = (page: number) => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key: string, value: any) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  const setSearch = (search?: string) => {
    filterChanged('search', search);
  };

  return {
    data,
    loading,
    error,
    fetchData,
    setData,
    filterChanged,
    filter,
    pageChanged,
    totalPages,
    totalData,
    search: filter.search,
    setSearch,
  };
};

interface ICustomerDetail extends ICustomer {
  memo: string;
  averageOrdersValue: number;
}

interface ICustomerDetailResponse {
  data: ICustomerDetail;
}

export const customerDetailsHook = (customerId: string) => {
  const [data, setData] = useState({} as ICustomerDetail);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const result: ICustomerDetailResponse =
        await CRMService.getById(customerId);
      setData(result?.data);
    } catch (error) {
      setError(error?.error?.msg || ErrorMessageConstant.defaultAPIError);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (customerId || refresh > 0) {
      fetchData();
    }
  }, [customerId, refresh]);

  return {
    data,
    loading,
    error,
    setData,
    setRefresh,
  };
};

interface IFulfillmentProvider {
  _id: string;
  type: string;
  name: string;
}

interface IOrder {
  _id: string;
  customer_id: string;
  status: string;
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
  displayId: string;
  totalItems: number;
  totalPrice: number;
  fulfillmentProvider: IFulfillmentProvider;
  marketplace: IMarketplace;
}

interface OrdersResponse {
  data: IOrder[];
}

export const customerOrderDetailsHook = (customerId: string) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const result: OrdersResponse = await CRMService.crmOrders(customerId);
      setData(result?.data);
    } catch (error) {
      setError(error?.error?.msg || ErrorMessageConstant.defaultAPIError);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (customerId || refresh > 0) {
      fetchData();
    }
  }, [customerId, refresh]);

  return {
    data,
    loading,
    error,
    setData,
    setRefresh,
  };
};

export interface IMarketplaceCustomer {
  _id: string;
  email: string;
  phone: string;
  name: string;
  address: ICustomerAddress;
  search?: string;
}

export interface IMarketplaceCustomersResponse {
  data: IMarketplaceCustomer[];
}

export const customersDropdownHook = (search: string) => {
  const [data, setData] = useState<IMarketplaceCustomer[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const result: IMarketplaceCustomersResponse =
        await CRMService.searchCustomers({
          search,
          limit: CommonConstant.defaultPageSize,
        });
      const mapped = result.data.map(d => ({ ...d, search }));
      setData(mapped);
    } catch (error) {
      setError(error.message || ErrorMessageConstant.defaultAPIError);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      setData([]);
      fetchData();
    },
    [search],
    500,
  );

  return { data, error, loading };
};
